import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CloudArrowUpIcon } from "@heroicons/react/24/outline";
import heic2any from "heic2any";
import { useAuth } from "../../contexts/AuthContext";
import {
  saveUserProfile,
  uploadProfileImage,
  uploadSponsorLogo,
} from "../../firebase";
import { UserProfile } from "../../types";
import logo from "../../assets/wattlenest.png";

const UserRegistration: React.FC = () => {
  const { currentUser, refreshUserStatus } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sponsorLogoInputRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState("");
  const [socialMediaLink, setSocialMediaLink] = useState("");
  const [sport, setSport] = useState("");
  const [bio, setBio] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [sponsorName, setSponsorName] = useState("");
  const [sponsorLogo, setSponsorLogo] = useState<File | null>(null);
  const [sponsorLogoPreviewUrl, setSponsorLogoPreviewUrl] = useState<
    string | null
  >(null);
  const [openToSponsorship, setOpenToSponsorship] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      try {
        let processedFile = file;
        
        // Check if the file is HEIC format and convert to JPEG
        if (file.type === 'image/heic' || file.type === 'image/heif' || 
            file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
          
          setError(null); // Clear any previous errors
          
          // Convert HEIC to JPEG
          const convertedBlob = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.8
          });
          
          // Create a new File object from the converted blob
          const convertedFile = new File(
            [convertedBlob as Blob], 
            file.name.replace(/\.(heic|heif)$/i, '.jpg'),
            { type: 'image/jpeg' }
          );
          
          processedFile = convertedFile;
        }
        
        setProfileImage(processedFile);

        // Create a preview URL for the selected/converted image
        const reader = new FileReader();
        reader.onload = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(processedFile);
        
      } catch (error) {
        console.error('Error processing image:', error);
        setError('Failed to process the image. Please try a different file.');
      }
    }
  };

  const handleSponsorLogoClick = () => {
    sponsorLogoInputRef.current?.click();
  };

  const handleSponsorLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      try {
        let processedFile = file;
        
        // Check if the file is HEIC format and convert to JPEG
        if (file.type === 'image/heic' || file.type === 'image/heif' || 
            file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
          
          setError(null); // Clear any previous errors
          
          // Convert HEIC to JPEG
          const convertedBlob = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.8
          });
          
          // Create a new File object from the converted blob
          const convertedFile = new File(
            [convertedBlob as Blob], 
            file.name.replace(/\.(heic|heif)$/i, '.jpg'),
            { type: 'image/jpeg' }
          );
          
          processedFile = convertedFile;
        }
        
        setSponsorLogo(processedFile);

        // Create a preview URL for the selected/converted image
        const reader = new FileReader();
        reader.onload = () => {
          setSponsorLogoPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(processedFile);
        
      } catch (error) {
        console.error('Error processing sponsor logo:', error);
        setError('Failed to process the sponsor logo. Please try a different file.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      setError("You must be logged in to complete registration");
      return;
    }

    if (!displayName.trim()) {
      setError("Name is required");
      return;
    }

    if (!profileImage) {
      setError("Profile picture is required");
      return;
    }

    if (!bio.trim()) {
      setError("Bio is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let photoURL = "";
      let sponsorLogoURL = "";

      // Upload profile image if one was selected
      if (profileImage) {
        photoURL = await uploadProfileImage(currentUser.uid, profileImage);
      }

      // Upload sponsor logo if one was selected
      if (sponsorLogo) {
        sponsorLogoURL = await uploadSponsorLogo(currentUser.uid, sponsorLogo);
      }

      // Create user profile object
      const userProfile: Partial<UserProfile> = {
        uid: currentUser.uid,
        displayName: displayName.trim(),
        email: currentUser.email || "",
        photoURL,
        socialMediaLink: socialMediaLink.trim(),
        sport: sport.trim(),
        bio: bio.trim(),
        description: description.trim(),
        location: location.trim(),
        sponsorName: sponsorName.trim(),
        sponsorLogoURL,
        openToSponsorship,
        isRegistrationComplete: true,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      // Save user profile to Firestore
      await saveUserProfile(currentUser.uid, userProfile);

      // Refresh user status
      await refreshUserStatus();

      // Redirect to portal dashboard
      navigate("/portal/dashboard");
    } catch (err: any) {
      setError(err.message || "Failed to complete registration");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto mt-10">
      <img alt="Wattlenest" src={logo} className="h-20 w-auto mx-auto" />
      <div className="bg-goldlight p-3 mb-5 mt-5 rounded-md">
        <h2 className="font-bold text-gray-900 text-center">
          Complete Your WattleNest Profile
        </h2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="flex flex-col items-center mb-6">
          {/* Profile Image Upload */}
          <div
            className="w-24 h-24 rounded-full bg-gold flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity mb-5 overflow-hidden"
            onClick={handleAvatarClick}
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt={displayName || "Profile"}
                className="w-full h-full object-cover"
              />
            ) : (
              <CloudArrowUpIcon className="w-8 h-8 text-white" />
            )}
          </div>

          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="sr-only"
          />

          <p className="text-sm text-gray-600 text-center mt-2">
            Click to upload profile picture (required)
          </p>
        </div>

        <div className="space-y-4">
          {/* Name Field */}
          <div className="mb-4">
            <label
              htmlFor="displayName"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Full Name *
            </label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Social Media Link Field */}
          <div className="mb-4">
            <label
              htmlFor="socialMediaLink"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Social Media Link
            </label>
            <input
              id="socialMediaLink"
              type="url"
              value={socialMediaLink}
              onChange={(e) => setSocialMediaLink(e.target.value)}
              placeholder="e.g. https://twitter.com/yourusername"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Sport Field */}
          <div className="mb-4">
            <label
              htmlFor="sport"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Sport
            </label>
            <input
              id="sport"
              type="text"
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              placeholder="What sport do you play?"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Bio Field */}
          <div className="mb-4">
            <label
              htmlFor="bio"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              My short bio (similar to IG bio)
            </label>

            <textarea
              id="bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              required
              placeholder="A short introduction to yourself"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Long Bio Field */}
          <div className="mb-4">
            <label
              htmlFor="bio"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              My Story
            </label>
            <textarea
              id="bio"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={10}
              required
              placeholder="Sporting background, acheivements, goals and interests. Tell your story..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Location Field */}
          <div className="mb-4">
            <label
              htmlFor="location"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Location
            </label>
            <input
              id="location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Where are you based?"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Sponsorship Section */}
          <div className="mb-4 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Sponsorship Information
            </h3>

            {/* Sponsor Name Field */}
            <div className="mb-4">
              <label
                htmlFor="sponsorName"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Sponsor Name
              </label>
              <input
                id="sponsorName"
                type="text"
                value={sponsorName}
                onChange={(e) => setSponsorName(e.target.value)}
                placeholder="Your current sponsor (if any)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Sponsor Logo Upload */}
            <div className="mb-4 flex flex-col items-center">
              <div
                onClick={handleSponsorLogoClick}
                className="w-30 h-30 border border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer overflow-hidden mb-2 hover:border-gray-400 transition-colors"
              >
                {sponsorLogoPreviewUrl ? (
                  <img
                    src={sponsorLogoPreviewUrl}
                    alt="Sponsor Logo"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <CloudArrowUpIcon className="w-8 h-8 text-gray-400" />
                )}
              </div>

              <input
                type="file"
                accept="image/*"
                ref={sponsorLogoInputRef}
                onChange={handleSponsorLogoChange}
                className="sr-only"
              />

              <p className="text-sm text-gray-600 text-center">
                Upload sponsor logo (if applicable)
              </p>
            </div>

            {/* Open to Sponsorship Switch */}
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={openToSponsorship}
                  onChange={(e) => setOpenToSponsorship(e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Open to sponsorship opportunities
                </span>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-10 mb-20 bg-gold text-white py-3 px-4 rounded-md hover:bg-gold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Loading...
                </div>
              ) : (
                "Complete Application"
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UserRegistration;
